import * as React from 'react';
import { Button, type ButtonProps } from './button';
import { cn } from '../../lib/utils';

export interface IdempotentButtonProps extends Omit<ButtonProps, 'onClick' | 'onSubmit'> {
  /**
   * Indicador externo (ej. mutation.isPending) — el botón también bloquea internamente
   * para prevenir doble dispatch antes de que el estado externo llegue al render.
   */
  isPending?: boolean;
  /**
   * Handler invocado al click. Se ignoran clicks adicionales hasta que la promesa devuelta
   * resuelva o `isPending` baje. Si retorna void/sync, el lock se libera en el siguiente tick.
   */
  onAction?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
  /** Texto/nodo a mostrar mientras la acción está in-flight. Default: children. */
  pendingLabel?: React.ReactNode;
}

function IdempotentButton({
  isPending: externalPending = false,
  onAction,
  pendingLabel,
  disabled,
  type = 'button',
  children,
  className,
  ...rest
}: IdempotentButtonProps) {
  const [internalPending, setInternalPending] = React.useState(false);
  const isPending = externalPending || internalPending;

  const handleClick = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isPending || !onAction) return;
      event.preventDefault();
      setInternalPending(true);
      try {
        await onAction(event);
      } finally {
        setInternalPending(false);
      }
    },
    [isPending, onAction],
  );

  return (
    <Button
      {...rest}
      type={type}
      disabled={disabled || isPending}
      aria-busy={isPending || undefined}
      onClick={handleClick}
      className={cn(className)}
    >
      {isPending && pendingLabel !== undefined ? pendingLabel : children}
    </Button>
  );
}

export { IdempotentButton };
